import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNil } from 'lodash-es';
import { ModelFormGroup } from 'src/app/typings/typed-forms';

interface TimeCtrlGroupValue {
  hours: number|null;
  minutes: number|null;
  seconds: string|null;
}


@Component({
  selector: 'me-time-input-cell',
  templateUrl: './time-input-cell.component.html',
  styleUrls: ['./time-input-cell.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputCellComponent),
      multi: true,
    },
  ],
})
export class TimeInputCellComponent implements AfterViewInit, ControlValueAccessor {

  lastKeyPressedEvent: KeyboardEvent;

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    this.lastKeyPressedEvent = event;
  }

  timeCtrlGroup: ModelFormGroup<TimeCtrlGroupValue> = this.fb.nonNullable.group({
    hours: [null as TimeCtrlGroupValue['hours']],
    minutes: [null as TimeCtrlGroupValue['minutes']],
    seconds: [null as TimeCtrlGroupValue['seconds']]
  })

  @Input() mode: 'tableCell' | 'ion-input' = 'tableCell';

  @HostBinding('class.tableCell')
  get isTableCell() {
    return this.mode === 'tableCell';
  }


  @HostListener('focusout')
  focusout() {
    this.onTouchedCallback();
  }

  @ViewChild('secondsInput')
  public secondsNativeInput: ElementRef<HTMLInputElement>;

  ionInputIsFocused = false;

  constructor(private fb: FormBuilder, private el: ElementRef) {}

  ngAfterViewInit(): void {
    if (this.secondsNativeInput) {
      setTimeout(() => {
        this.secondsNativeInput.nativeElement.focus();
        this.secondsNativeInput.nativeElement.setSelectionRange(0, this.secondsNativeInput.nativeElement.value.length);
      });
    }
  }

  private onTouchedCallback: () => void;


  writeValue(seconds: number): void {
    if (!isNil(seconds)) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      this.timeCtrlGroup.setValue({
        hours,
        minutes,
        seconds: remainingSeconds.toString()
      });
    } else {
      this.timeCtrlGroup.reset();
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this.timeCtrlGroup.valueChanges.subscribe(value => {
      fn(this.getValue());
    });
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.timeCtrlGroup.disable() : this.timeCtrlGroup.enable();
  }

  getValue() {
    let seconds = 0;

    if (this.timeCtrlGroup.value.hours) {
      seconds += this.timeCtrlGroup.value.hours * 3600;
    }

    if (this.timeCtrlGroup.value.minutes) {
      seconds += this.timeCtrlGroup.value.minutes * 60;
    }

    if (this.timeCtrlGroup.value.seconds) {
      seconds += parseInt(this.timeCtrlGroup.value.seconds);
    }

    return seconds;
  }

  onIonInputFocus() {
    this.ionInputIsFocused = true;
  }
  onIonInputBlur() {
    this.ionInputIsFocused = false;
  }
}
